import { Interface } from '@ethersproject/abi'
import { TransactionResponse } from '@ethersproject/abstract-provider'
import { BigNumber } from '@ethersproject/bignumber'
import { AddressZero } from '@ethersproject/constants'
import { CustomUserProperties, SwapEventName } from '@uniswap/analytics-events'
import { Percent } from '@uniswap/sdk-core'
import { FlatFeeOptions, SwapRouter, UNIVERSAL_ROUTER_ADDRESS } from '@uniswap/universal-router-sdk'
import { FeeOptions, toHex } from '@uniswap/v3-sdk'
import { useTotalBalancesUsdForAnalytics } from 'graphql/data/apollo/TokenBalancesProvider'
import { useAccount } from 'hooks/useAccount'
import { useEthersWeb3Provider } from 'hooks/useEthersProvider'
import { useGetTransactionDeadline } from 'hooks/useTransactionDeadline'
import { t } from 'i18n'
import useBlockNumber from 'lib/hooks/useBlockNumber'
import { formatCommonPropertiesForTrade, formatSwapSignedAnalyticsEventProperties } from 'lib/utils/analytics'
import { useCallback } from 'react'
import { ClassicTrade, TradeFillType } from 'state/routing/types'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { trace } from 'tracing/trace'
import { sendAnalyticsEvent } from 'uniswap/src/features/telemetry/send'
import { logger } from 'utilities/src/logger/logger'
import { useTrace } from 'utilities/src/telemetry/trace/TraceContext'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { UserRejectedRequestError, WrongChainError } from 'utils/errors'
import isZero from 'utils/isZero'
import { didUserReject, swapErrorToUserReadableMessage } from 'utils/swapErrorToUserReadableMessage'
import { getWalletMeta } from 'utils/walletMeta'
import { validateKyberSwapTransaction } from 'utils/kyberSwapValidation'
import { assertKyberBuildSafe } from 'utils/kyberBuildPolicy'
import { isKibaRouterBuildEnabled } from 'utils/robinhoodRouter/flags'
import { buildRouterSwapTransaction } from 'hooks/robinhoodRouterBuild'
import { toRouterSlippageBps, toRouterDeadlineSeconds } from 'utils/robinhoodRouter/buildParams'
import { PermitSignature } from './usePermitAllowance'

const PROXY_ABI = new Interface([
  'function swap(address router, bytes data, address tokenIn, uint256 amountIn) payable',
])

/** Thrown when gas estimation fails. This class of error usually requires an emulator to determine the root cause. */
class GasEstimationError extends Error {
  constructor() {
    super(t('swap.error.expectedToFail'))
  }
}

/**
 * Thrown when the user modifies the transaction in-wallet before submitting it.
 * In-wallet calldata modification nullifies any safeguards (eg slippage) from the interface, so we recommend reverting them immediately.
 */
class ModifiedSwapError extends Error {
  constructor() {
    super(t('swap.error.modifiedByWallet'))
  }
}

interface SwapOptions {
  slippageTolerance: Percent
  permit?: PermitSignature
  feeOptions?: FeeOptions
  flatFeeOptions?: FlatFeeOptions
}

/**
 * Fail-closed guard for the Robinhood router execution branch (F4, M4
 * audit). A trade carrying `robinhoodRouterData` has a synthetic display
 * route and must never be encoded by the generic Kyber/SDK path below —
 * that fallthrough previously happened whenever `isKibaRouterBuildEnabled()`
 * was false, relying solely on the swap button being disabled to prevent
 * it. This throws before any build/estimate/send call is made, independent
 * of UI state.
 */
export function assertRouterExecutionAllowed(trade: Pick<ClassicTrade, 'robinhoodRouterData'>): void {
  if (trade.robinhoodRouterData && !isKibaRouterBuildEnabled()) {
    throw new Error('Robinhood router execution is disabled');
  }
}

export function useUniversalRouterSwapCallback(
  trade: ClassicTrade | undefined,
  fiatValues: { amountIn?: number; amountOut?: number; feeUsd?: number },
  options: SwapOptions
) {
  const account = useAccount();
  const provider = useEthersWeb3Provider();
  const connectorName = useAccount().connector?.name;

  const analyticsContext = useTrace();
  const blockNumber = useBlockNumber();
  const getDeadline = useGetTransactionDeadline();
  const isAutoSlippage = useUserSlippageTolerance()[0] === 'auto';
  const portfolioBalanceUsd = useTotalBalancesUsdForAnalytics();

  return useCallback(
    (): Promise<{ type: TradeFillType.Classic; response: TransactionResponse; deadline?: BigNumber }> =>
      trace({ name: 'Swap (Classic)', op: 'swap.classic' }, async (trace) => {
        try {
          if (account.status !== 'connected') {
            throw new Error('wallet not connected');
          }
          if (!provider) {
            throw new Error('missing provider');
          }
          if (!trade) {
            throw new Error('missing trade');
          }
          const connectedChainId = await provider.getSigner().getChainId();
          if (account.chainId !== connectedChainId) {
            throw new WrongChainError();
          }

          const deadline = await getDeadline();

          trace.setData('slippageTolerance', options.slippageTolerance.toFixed(2));

          if (trade.robinhoodRouterData) {
            // Fail closed: a trade carrying `robinhoodRouterData` must NEVER
            // reach the generic Kyber/SDK encoder below, regardless of how
            // this callback was reached (button state, a stale modal, a
            // direct call). The build flag only gates whether we're allowed
            // to actually execute the router build; it does not gate whether
            // the trade's route is safe to hand to the generic encoder — it
            // never is, since `robinhoodRouterData`'s route is a synthetic
            // display route (see F4, M4 audit).
            assertRouterExecutionAllowed(trade);

            // Robinhood router execution path: the router already fetched a
            // fresh, server-simulated build (min-out, deadline, deterioration
            // all enforced server-side) — see buildRouterSwapTransaction. We
            // do not re-estimate gas or re-encode calldata client-side; we
            // just send exactly what the router returned.
            // Convert + clamp to the router's /v1/build contract (audit #9) so an out-of-range
            // slippage/deadline can't cause a deterministic INVALID_REQUEST at submission time.
            const userSlippageBps = toRouterSlippageBps(
              options.slippageTolerance.numerator.toString(),
              options.slippageTolerance.denominator.toString(),
            );
            const DEFAULT_DEADLINE_SECONDS = 300;
            const nowSeconds = Math.floor(Date.now() / 1000);
            const deadlineSeconds = toRouterDeadlineSeconds(
              deadline ? deadline.toNumber() - nowSeconds : DEFAULT_DEADLINE_SECONDS,
            );

            const routerTx = await buildRouterSwapTransaction(trade.robinhoodRouterData, {
              sender: account.address,
              recipient: account.address,
              slippageBps: userSlippageBps,
              deadlineSeconds,
            });

            const response = await trace.child(
              { name: 'Send transaction', op: 'wallet.send_transaction' },
              async (walletTrace) => {
                try {
                  return await provider.getSigner().sendTransaction({
                    to: routerTx.to,
                    data: routerTx.data,
                    ...(routerTx.value && routerTx.value !== '0' ? { value: toHex(routerTx.value) } : {}),
                    gasLimit: routerTx.gasLimit,
                  });
                } catch (error) {
                  if (didUserReject(error)) {
                    walletTrace.setStatus('cancelled');
                    throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error));
                  } else {
                    throw error;
                  }
                }
              }
            );

            return { type: TradeFillType.Classic as const, response, deadline: undefined };
          }

          let tx: { from: string; to: string; data: string; value?: string };

          if (trade.kyberSwapData) {
            // KyberSwap execution path: fetch fresh calldata from the worker
            const kyber = trade.kyberSwapData;
            // Leg 1: pass the user's UNMODIFIED slippage tolerance — the app
            // fee is modeled as a fee (see kyberMinimumAmountOut), never as a
            // slippage widening. This bps value is the exact field the
            // worker forwards into Kyber's route/build `slippageTolerance`
            // param (worker.js's handleKyberBuild, ~line 325), which is what
            // controls the on-chain encoded minimum.
            const slippageBps = Math.round(
              Number(options.slippageTolerance.numerator.toString()) /
              Number(options.slippageTolerance.denominator.toString()) * 10000
            );
            const userSlippageBps = isNaN(slippageBps) ? 50 : slippageBps;
            const GATEWAY_URL = process.env.REACT_APP_UNISWAP_GATEWAY_DNS;
            const buildResp = await fetch(`${GATEWAY_URL}/kyber-build`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                tokenIn: kyber.tokenIn,
                tokenOut: kyber.tokenOut,
                amount: kyber.amount,
                chainId: kyber.chainId,
                sender: account.address,
                slippageTolerance: userSlippageBps,
              }),
            });
            if (!buildResp.ok) {
              const errBody = await buildResp.text();
              throw new Error(`KyberSwap build failed: ${errBody}`);
            }
            const buildData = await buildResp.json();

            // Validate the inner KyberSwap calldata (router-level)
            const validationTarget = buildData.useProxy ? buildData.kyberRouter : buildData.to;
            const validation = validateKyberSwapTransaction({
              to: validationTarget,
              data: buildData.data,
              value: buildData.value || '0',
              tokenIn: kyber.tokenIn,
              tokenOut: kyber.tokenOut,
              sender: buildData.useProxy ? buildData.to : account.address,
              chainId: kyber.chainId,
              quotedAmountOut: trade.outputAmount.quotient.toString(),
              buildAmountOut: buildData.amountOut || '0',
            });
            if (!validation.valid) {
              throw new Error(
                `KyberSwap safety check failed: ${validation.reason} [${validation.code}]. Transaction blocked to protect funds.`
              );
            }

            // Fee metadata: the worker (worker.js) is the authoritative
            // source of fee config, not this file's hardcoded constants. It
            // attaches `feeMeta` to both the original quote (kyber.feeMeta)
            // and this fresh build (buildData.feeMeta). assertKyberBuildSafe
            // (utils/kyberBuildPolicy.ts) is the transaction-safety boundary:
            // it prefers the quote's feeMeta, falls back to the build's
            // feeMeta, then to hardcoded fallback constants for workers that
            // don't send this field yet, and rejects the trade outright if
            // quote-time and build-time feeMeta are both present but
            // disagree (fee config changed between quote and build) or if
            // the fresh build's amountOut has fallen below the enforceable
            // minimum computed from the original quote. This check is
            // separate from - and does not govern - what the UI displays:
            // the fee shown to the user is bound to the same feeMeta
            // independently in state/routing/utils.ts (getSwapFee), so
            // display and enforcement read the same source but are enforced
            // as two distinct requirements.
            const quotedAmountOutBigInt = BigInt(trade.outputAmount.quotient.toString());
            const buildAmountOutBigInt = BigInt(buildData.amountOut || '0');
            assertKyberBuildSafe({
              quotedAmountOut: quotedAmountOutBigInt,
              buildAmountOut: buildAmountOutBigInt,
              quoteFeeMetaRaw: kyber.feeMeta,
              buildFeeMetaRaw: buildData.feeMeta,
              slippageBps: BigInt(userSlippageBps),
              // Mirrors worker.js's kyberFeeMeta(nativeInput) selection so the
              // fallback (used only when both quote/build feeMeta are absent)
              // resolves the same chargeFeeBy the worker would have picked.
              isNativeInput: trade.inputAmount.currency.isNative,
            });

            if (buildData.useProxy) {
              const isNativeIn = buildData.value && buildData.value !== '0';
              const proxyCalldata = PROXY_ABI.encodeFunctionData('swap', [
                buildData.kyberRouter,
                buildData.data,
                isNativeIn ? AddressZero : kyber.tokenIn,
                isNativeIn ? '0' : buildData.amountIn,
              ]);
              tx = {
                from: account.address,
                to: buildData.to,
                data: proxyCalldata,
                ...(isNativeIn ? { value: toHex(buildData.value) } : {}),
              };
            } else {
              tx = {
                from: account.address,
                to: buildData.to,
                data: buildData.data,
                ...(buildData.value && buildData.value !== '0' ? { value: toHex(buildData.value) } : {}),
              };
            }
          } else {
            const { calldata: data, value } = SwapRouter.swapERC20CallParameters(trade, {
              slippageTolerance: options.slippageTolerance,
              deadlineOrPreviousBlockhash: deadline?.toString(),
              inputTokenPermit: options.permit,
              fee: options.feeOptions,
              flatFee: options.flatFeeOptions,
              chainId: account.chainId,
            });
            tx = {
              from: account.address,
              to: UNIVERSAL_ROUTER_ADDRESS(account.chainId),
              data,
              // TODO (https://github.com/Uniswap/universal-router-sdk/issues/113): universal-router-sdk returns a non-hexlified value.
              ...(value && !isZero(value) ? { value: toHex(value) } : {}),
            };
          }

          let gasLimit: BigNumber;
          try {
            const gasEstimate = await provider.estimateGas(tx);
            gasLimit = calculateGasMargin(gasEstimate);
            trace.setData('gasLimit', gasLimit.toNumber());
          } catch (gasError) {
            sendAnalyticsEvent(SwapEventName.SWAP_ESTIMATE_GAS_CALL_FAILED, {
              ...formatCommonPropertiesForTrade(trade, options.slippageTolerance),
              ...analyticsContext,
              client_block_number: blockNumber,
              txRequest: tx,
              isAutoSlippage,
            });
            const wrappedError = new Error('gas error', { cause: gasError });
            logger.error(wrappedError, {
              tags: {
                file: 'useUniversalRouter',
                function: 'useUniversalRouterSwapCallback',
              },
            });
            throw new GasEstimationError();
          }

          const response = await trace.child(
            { name: 'Send transaction', op: 'wallet.send_transaction' },
            async (walletTrace) => {
              try {
                return await provider.getSigner().sendTransaction({ ...tx, gasLimit });
              } catch (error) {
                if (didUserReject(error)) {
                  walletTrace.setStatus('cancelled');
                  throw new UserRejectedRequestError(swapErrorToUserReadableMessage(error));
                } else {
                  throw error;
                }
              }
            }
          );
          sendAnalyticsEvent(SwapEventName.SWAP_SIGNED, {
            ...formatSwapSignedAnalyticsEventProperties({
              trade,
              timeToSignSinceRequestMs: trace.now(),
              allowedSlippage: options.slippageTolerance,
              fiatValues,
              txHash: response.hash,
              portfolioBalanceUsd,
            }),
            ...analyticsContext,
            // TODO (WEB-2993): remove these after debugging missing user properties.
            [CustomUserProperties.WALLET_ADDRESS]: account.address,
            [CustomUserProperties.WALLET_TYPE]: connectorName,
            [CustomUserProperties.PEER_WALLET_AGENT]: provider ? getWalletMeta(provider)?.agent : undefined,
          });
          if (tx.data !== response.data) {
            sendAnalyticsEvent(SwapEventName.SWAP_MODIFIED_IN_WALLET, {
              txHash: response.hash,
              expected: tx.data,
              actual: response.data,
              ...analyticsContext,
            });
            if (!response.data || response.data.length === 0 || response.data === '0x') {
              throw new ModifiedSwapError();
            }
          }
          return { type: TradeFillType.Classic as const, response, deadline };
        } catch (error: unknown) {
          if (error instanceof GasEstimationError) {
            throw error;
          } else if (error instanceof UserRejectedRequestError) {
            trace.setStatus('cancelled');
            throw error;
          } else if (error instanceof ModifiedSwapError) {
            trace.setError(error, 'data_loss');
            throw error;
          } else {
            trace.setError(error);
            throw Error(swapErrorToUserReadableMessage(error));
          }
        }
      }),
    [
      account.status,
      account.chainId,
      account.address,
      provider,
      trade,
      getDeadline,
      options.slippageTolerance,
      options.permit,
      options.feeOptions,
      options.flatFeeOptions,
      fiatValues,
      portfolioBalanceUsd,
      analyticsContext,
      connectorName,
      blockNumber,
      isAutoSlippage,
    ]
  );
}